import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { User, Mail, Building, Users, Hash, Save, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ProfileSettings({ userInfo }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    npn: '',
    status: '',
    fflAgency_name: '',
    agentUpline_name: '',
    agentUpline_npn: '',
    ghlLocation_id: '',
    ghlLocation_name: ''
  });
  const [isSaving, setIsSaving] = useState({
    personal: false,
    agent: false,
    ghl: false,
    agency: false
  });
  const [isVerifyingNPN, setIsVerifyingNPN] = useState(false);

  useEffect(() => {
    if (userInfo) {
      setFormData({
        firstName: userInfo.first_name || '',
        lastName: userInfo.last_name || '',
        email: userInfo.email || '',
        npn: userInfo.npn || '',
        status: userInfo.status || 'disconnected',
        fflAgency_name: userInfo.fflAgency_name || '',
        agentUpline_name: userInfo.agentUpline_name || '',
        agentUpline_npn: userInfo.agentUpline_npn || '',
        ghlLocation_id: userInfo.ghlLocation_id || '',
        ghlLocation_name: userInfo.ghlLocation_name || ''
      });
    }
  }, [userInfo]);

  const getInitials = () => {
    if (formData.firstName && formData.lastName) {
      return `${formData.firstName[0]}${formData.lastName[0]}`.toUpperCase();
    }
    return 'U';
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'connected':
        return 'bg-green-500';
      case 'disconnected':
        return 'bg-red-500';
      case 'pending':
        return 'bg-yellow-500';
      default:
        return 'bg-neutral-500';
    }
  };

  const handleSavePersonal = async () => {
    setIsSaving({ ...isSaving, personal: true });
    try {
      // TODO: Implement API call to update profile
      const updatedUserInfo = {
        ...userInfo,
        first_name: formData.firstName,
        last_name: formData.lastName,
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email
      };
      localStorage.setItem('user_info', JSON.stringify(updatedUserInfo));
      
      toast.success('Personal information updated successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to update personal information');
    } finally {
      setIsSaving({ ...isSaving, personal: false });
    }
  };

  const handleVerifyAndSaveNPN = async () => {
    if (!formData.npn || formData.npn.trim().length === 0) {
      toast.error('Please enter an NPN number');
      return;
    }

    setIsVerifyingNPN(true);
    try {
      // TODO: Implement NPN verification API call
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const updatedUserInfo = {
        ...userInfo,
        npn: formData.npn
      };
      localStorage.setItem('user_info', JSON.stringify(updatedUserInfo));
      
      toast.success('NPN verified and saved successfully!');
    } catch (error) {
      console.error('Error verifying NPN:', error);
      toast.error('Failed to verify NPN');
    } finally {
      setIsVerifyingNPN(false);
    }
  };

  const handleSaveGHL = async () => {
    setIsSaving({ ...isSaving, ghl: true });
    try {
      // TODO: Implement API call to update GHL location
      const updatedUserInfo = {
        ...userInfo,
        ghlLocation_id: formData.ghlLocation_id,
        ghlLocation_name: formData.ghlLocation_name
      };
      localStorage.setItem('user_info', JSON.stringify(updatedUserInfo));
      
      toast.success('GHL location updated successfully!');
    } catch (error) {
      console.error('Error saving GHL location:', error);
      toast.error('Failed to update GHL location');
    } finally {
      setIsSaving({ ...isSaving, ghl: false });
    }
  };

  const handleSaveAgency = async () => {
    setIsSaving({ ...isSaving, agency: true });
    try {
      // TODO: Implement API call to update agency info
      const updatedUserInfo = {
        ...userInfo,
        fflAgency_name: formData.fflAgency_name,
        agentUpline_name: formData.agentUpline_name,
        agentUpline_npn: formData.agentUpline_npn
      };
      localStorage.setItem('user_info', JSON.stringify(updatedUserInfo));
      
      toast.success('Agency information updated successfully!');
    } catch (error) {
      console.error('Error saving agency info:', error);
      toast.error('Failed to update agency information');
    } finally {
      setIsSaving({ ...isSaving, agency: false });
    }
  };

  const hasNPN = formData.npn && String(formData.npn).trim().length > 0;
  const hasGHLLocation = formData.ghlLocation_id && String(formData.ghlLocation_id).trim().length > 0;

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-neutral-800">Profile Settings</h2>
        <p className="text-neutral-600 mt-1">Manage your account information and preferences</p>
      </div>

      {/* Profile Overview Card */}
      <Card className="rounded-2xl">
        <CardContent className="pt-6">
          <div className="flex items-center gap-6">
            <div className="relative">
              <Avatar className="w-24 h-24 border-4 border-[#d4af37]">
                <AvatarFallback className="bg-[#f4e5b8] text-[#b8941e] text-2xl font-bold">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
              <div className={`absolute bottom-1 right-1 w-5 h-5 ${getStatusColor(formData.status)} rounded-full border-2 border-white`}></div>
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-neutral-800">
                {formData.firstName} {formData.lastName}
              </h3>
              <p className="text-neutral-600 mt-1">{formData.email || 'No email provided'}</p>
              <div className="flex gap-2 mt-3">
                <Badge className="bg-[#d4af37] text-white">
                  NPN: {formData.npn || 'Not verified'}
                </Badge>
                <Badge variant="outline" className={`border-2 ${
                  formData.status === 'connected' ? 'border-green-500 text-green-700 bg-green-50' :
                  formData.status === 'pending' ? 'border-yellow-500 text-yellow-700 bg-yellow-50' :
                  'border-red-500 text-red-700 bg-red-50'
                }`}>
                  {formData.status?.toUpperCase() || 'DISCONNECTED'}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Personal Information */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-neutral-800">
              <User className="w-5 h-5 text-[#d4af37]" />
              Personal Information
            </CardTitle>
            <CardDescription>Update your personal details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-neutral-700">First Name</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-neutral-700">Last Name</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-neutral-700">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 rounded-xl"
                />
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSavePersonal}
                disabled={isSaving.personal}
                className="bg-gradient-to-r from-[#d4af37] to-[#b8941e] hover:from-[#b8941e] hover:to-[#d4af37] text-white rounded-xl"
              >
                {isSaving.personal ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Agent Information */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-neutral-800">
              <Hash className="w-5 h-5 text-[#d4af37]" />
              Agent Information
            </CardTitle>
            <CardDescription>Your agent credentials</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-neutral-700">NPN Number</Label>
              <Input
                value={formData.npn}
                onChange={(e) => setFormData({ ...formData, npn: e.target.value })}
                disabled={hasNPN}
                placeholder={!hasNPN ? "Enter your NPN number" : ""}
                className={`rounded-xl ${hasNPN ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
              />
              {!hasNPN && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Add and verify your NPN to unlock more features
                </p>
              )}
              {hasNPN && (
                <p className="text-xs text-neutral-500">NPN cannot be changed once verified</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-neutral-700">Account Status</Label>
              <Input
                value={formData.status?.toUpperCase() || 'DISCONNECTED'}
                disabled
                className="rounded-xl bg-neutral-100 cursor-not-allowed"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-neutral-700">User ID</Label>
              <Input
                value={userInfo?.user_id || 'N/A'}
                disabled
                className="rounded-xl bg-neutral-100 cursor-not-allowed text-xs"
              />
            </div>
            {!hasNPN && (
              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleVerifyAndSaveNPN}
                  disabled={isVerifyingNPN || !formData.npn}
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl"
                >
                  {isVerifyingNPN ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Verify & Save NPN
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* GHL Location Information */}
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-neutral-800">
            <Building className="w-5 h-5 text-[#d4af37]" />
            GHL Location Information
          </CardTitle>
          <CardDescription>Your GoHighLevel location details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-neutral-700">Location Name</Label>
              <Input
                value={formData.ghlLocation_name}
                onChange={(e) => setFormData({ ...formData, ghlLocation_name: e.target.value })}
                disabled={hasGHLLocation || !hasNPN}
                placeholder={!hasGHLLocation && hasNPN ? "Enter location name" : ""}
                className={`rounded-xl ${(hasGHLLocation || !hasNPN) ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-neutral-700">Location ID</Label>
              <Input
                value={formData.ghlLocation_id}
                onChange={(e) => setFormData({ ...formData, ghlLocation_id: e.target.value })}
                disabled={hasGHLLocation || !hasNPN}
                placeholder={!hasGHLLocation && hasNPN ? "Enter location ID" : ""}
                className={`rounded-xl text-xs ${(hasGHLLocation || !hasNPN) ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
              />
            </div>
          </div>
          {!hasNPN && (
            <p className="text-xs text-amber-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Please verify your NPN number first to edit GHL location
            </p>
          )}
          {hasNPN && !hasGHLLocation && (
            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSaveGHL}
                disabled={isSaving.ghl || !formData.ghlLocation_name || !formData.ghlLocation_id}
                className="bg-gradient-to-r from-[#d4af37] to-[#b8941e] hover:from-[#b8941e] hover:to-[#d4af37] text-white rounded-xl"
              >
                {isSaving.ghl ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Agency & Upline Information */}
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-neutral-800">
            <Users className="w-5 h-5 text-[#d4af37]" />
            Agency & Upline Information
          </CardTitle>
          <CardDescription>Your agency hierarchy details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-neutral-700 flex items-center gap-2">
                  <Building className="w-4 h-4 text-neutral-500" />
                  Agency Name
                </Label>
                <Input
                  value={formData.fflAgency_name}
                  onChange={(e) => setFormData({ ...formData, fflAgency_name: e.target.value })}
                  disabled={!hasNPN}
                  placeholder={hasNPN ? "Enter agency name" : ""}
                  className={`rounded-xl ${!hasNPN ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
                />
              </div>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-neutral-700 flex items-center gap-2">
                  <Users className="w-4 h-4 text-neutral-500" />
                  Upline Agent Name
                </Label>
                <Input
                  value={formData.agentUpline_name}
                  onChange={(e) => setFormData({ ...formData, agentUpline_name: e.target.value })}
                  disabled={!hasNPN}
                  placeholder={hasNPN ? "Enter upline name" : ""}
                  className={`rounded-xl ${!hasNPN ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-neutral-700">Upline NPN</Label>
                <Input
                  value={formData.agentUpline_npn}
                  onChange={(e) => setFormData({ ...formData, agentUpline_npn: e.target.value })}
                  disabled={!hasNPN}
                  placeholder={hasNPN ? "Enter upline NPN" : ""}
                  className={`rounded-xl ${!hasNPN ? 'bg-neutral-100 cursor-not-allowed' : ''}`}
                />
              </div>
            </div>
          </div>
          {!hasNPN && (
            <p className="text-xs text-amber-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Please verify your NPN number first to edit agency information
            </p>
          )}
          {hasNPN && (
            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSaveAgency}
                disabled={isSaving.agency}
                className="bg-gradient-to-r from-[#d4af37] to-[#b8941e] hover:from-[#b8941e] hover:to-[#d4af37] text-white rounded-xl"
              >
                {isSaving.agency ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
